import React, { Suspense, useEffect } from "react";

import { Category } from "@faire/retailer/models/Category/Category";
import { getSettingAndAssignEnableSDUIHubPage } from "@faire/retailer/settings/getSettingEnableSDUIHubPage";
import { useAssignSettingEnableSDUIWebCategoryPage } from "@faire/retailer/settings/useSettingEnableSDUIWebCategoryPage";
import { CategoryPageRequestParamsProvider } from "@faire/retailer/views/category/__internal__/CategoryPageRoot/__internal__/LoggedInCategoryPage/__internal__/Base/CategoryPageRequestParamsProvider";
import { useLoggedInCategoryPageBase } from "@faire/retailer/views/category/__internal__/CategoryPageRoot/__internal__/LoggedInCategoryPage/__internal__/Base/LazyLoggedInCategoryPageBase";
import { useSDUICategoryPageBase } from "@faire/retailer/views/category/__internal__/CategoryPageRoot/__internal__/LoggedInCategoryPage/__internal__/Base/LazySDUICategoryPageBase";
import { CategoryPagePrefetcher } from "@faire/retailer/views/category/__internal__/CategoryPageRoot/__internal__/LoggedInCategoryPage/__internal__/CategoryPagePrefetcher";
import { CurrentCategoryPageContextProvider } from "@faire/retailer/views/category/__internal__/CategoryPageRoot/__internal__/LoggedInCategoryPage/__internal__/currentCategoryPageContext";
import { SDUICategoryPagePrefetcher } from "@faire/retailer/views/category/__internal__/CategoryPageRoot/__internal__/LoggedInCategoryPage/__internal__/SDUICategoryPagePrefetcher";
import { useIsSDUISeasonalHubPage } from "@faire/retailer/views/category/__internal__/CategoryPageRoot/__internal__/LoggedInCategoryPage/__internal__/useIsSDUISeasonalHubPage";
import { useShouldEnableSDUIProductFirstPage } from "@faire/retailer/views/category/__internal__/CategoryPageRoot/__internal__/LoggedInCategoryPage/__internal__/useShouldEnableSDUIProductFirstPage";
import { LoggedInCategoryPageLoadingSkeleton } from "@faire/retailer/views/category/__internal__/CategoryPageRoot/CategoryPageLoadingSkeleton/LoggedInCategoryPageLoadingSkeleton";

interface IProps {
  category: Category;
  navigationPath: string;
}

export const LoggedInCategoryPage = (props: IProps) => {
  const { category, navigationPath } = props;

  return (
    <CurrentCategoryPageContextProvider value={{ category, navigationPath }}>
      <CategoryPageRequestParamsProvider>
        <LoggedInCategoryPageBase />
      </CategoryPageRequestParamsProvider>
    </CurrentCategoryPageContextProvider>
  );
};

const LoggedInCategoryPageBase = () => {
  const LoggedInCategoryPageBase = useLoggedInCategoryPageBase();
  const SDUICategoryPageBase = useSDUICategoryPageBase();

  const {
    isEligibleForSDUIProductFirstPage,
    shouldEnableSDUIProductFirstPage,
  } = useShouldEnableSDUIProductFirstPage();
  const assignSDUIWebCategoryPage = useAssignSettingEnableSDUIWebCategoryPage();

  const isSDUISeasonalHubPage = useIsSDUISeasonalHubPage();
  const shouldRenderSDUICategoryPage =
    (isSDUISeasonalHubPage && getSettingAndAssignEnableSDUIHubPage()) ||
    shouldEnableSDUIProductFirstPage;

  useEffect(() => {
    if (isEligibleForSDUIProductFirstPage) {
      assignSDUIWebCategoryPage();
    }
  }, [assignSDUIWebCategoryPage, isEligibleForSDUIProductFirstPage]);

  return (
    <>
      {shouldRenderSDUICategoryPage ? (
        <SDUICategoryPagePrefetcher />
      ) : (
        <CategoryPagePrefetcher />
      )}

      <Suspense
        fallback={
          <LoggedInCategoryPageLoadingSkeleton
            isFullWidthCategoryPage={!isSDUISeasonalHubPage}
          />
        }
      >
        {shouldRenderSDUICategoryPage ? (
          <SDUICategoryPageBase />
        ) : (
          <LoggedInCategoryPageBase />
        )}
      </Suspense>
    </>
  );
};
